import type React from "react";

import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { MUISwitch } from "shared/atoms/MUI/MUISwitch";

import { ProfileRow } from "../shared/ProfileRow";

export const NewPatientNotificationSwitch: React.FC = () => {
  const { profile, setProfile } = useProfileContext();
  const { t } = useTranslation();
  const putTherapistMutation = usePutTherapistMutation();

  invariant(profile);

  // Note! The server store the "ignore"-value while we are displaying the
  // opposite to enable notifications / disable notifications.
  const isIgnoreEmail = Boolean(profile.therapist_profile.ignore_new_patient_email);
  const isEnabled = !isIgnoreEmail;

  const onSubmit = (ignoreNewPatientEmail: boolean) => {
    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        ignore_new_patient_email: ignoreNewPatientEmail,
      },
    };

    // Optimistically update the profile for immediate UI feedback.
    // Then onSuccess we update the profile with "real" server state.
    setProfile({
      ...profile,
      therapist_profile: {
        ...profile.therapist_profile,
        ...data.therapist_profile,
      },
    });

    const variables = { therapistId, data };
    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);
      },
    });
  };

  return (
    <ProfileRow
      label={t("therapist_settings.new_patient_notification_switch")}
      labelTestId="new-patient-email-header"
      value={
        <MUISwitch
          data-testid="new-patient-email-switch"
          checked={isEnabled}
          onChange={() => {
            onSubmit(!isIgnoreEmail);
          }}
        />
      }
      errorMessage={putTherapistMutation.isError ? t("errors.generic") : undefined}
      dataTestId="new-patient-email"
    />
  );
};
